import { Stack, usePathname, useRootNavigationState, useRouter, useSegments } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { AppSplash } from '@/components/AppSplash';
import { MobileWebDownloadBanner } from '@/components/MobileWebDownloadBanner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BugReportProvider } from '@/contexts/BugReportContext';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { NeedsActionProvider } from '@/contexts/NeedsActionContext';
import { ProfileProvider } from '@/contexts/ProfileContext';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import { AppAlertProvider } from '@/lib/appAlert';

// Αρχικοποίηση i18n (side-effect import — πρέπει να τρέξει πριν οποιοδήποτε t())
import '@/i18n';

function useProtectedRoute() {
  const { session, initializing, passwordRecovery } = useAuth();
  const { languageReady, hasStoredLanguage } = useLanguage();
  const segments = useSegments() as string[];
  const router = useRouter();
  const navState = useRootNavigationState();

  useEffect(() => {
    if (!navState?.key) return;
    if (initializing) return;
    if (!languageReady) return;

    const onLanguageSelect = segments[0] === 'language-select';
    const inAuthGroup = segments[0] === '(auth)';
    const isPublic =
      segments[0] === 'legal' ||
      segments[0] === 'verify' ||
      (segments[0] === '(auth)' &&
        (segments[1] === 'invite' ||
          segments[1] === 'offer-invite' ||
          segments[1] === 'agreement-invite'));

    // 1. Επιλογή γλώσσας: έχει προτεραιότητα πάνω από όλα (εκτός password recovery)
    if (!hasStoredLanguage && !onLanguageSelect && !isPublic) {
      router.replace('/language-select');
      return;
    }

    // 1b. Γλώσσα μόλις επιλέχθηκε — φύγε από language-select
    if (hasStoredLanguage && onLanguageSelect) {
      router.replace(session ? '/(app)' : '/(auth)/login');
      return;
    }

    // 2. Password recovery
    if (passwordRecovery) {
      const onReset = segments[0] === '(auth)' && segments[1] === 'reset-password';
      if (!onReset) router.replace('/(auth)/reset-password');
      return;
    }

    // 3. Φύλαξη: redirect αν δεν είναι συνδεδεμένος
    if (!session && !inAuthGroup && !isPublic && !onLanguageSelect) {
      router.replace('/(auth)/login');
    } else if (session && inAuthGroup) {
      router.replace('/(app)');
    }
  }, [session, initializing, passwordRecovery, segments, router, navState?.key, languageReady, hasStoredLanguage]);
}

// Στο web, όταν αλλάζει route, το προηγούμενο focused στοιχείο (π.χ. ένα
// κουμπί) μπορεί να κρατήσει focus ενώ το περιεχόμενό του γίνεται aria-hidden
// από το routing — ο browser βγάζει προειδοποίηση προσβασιμότητας. Κάνουμε
// blur το ενεργό στοιχείο σε κάθε αλλαγή pathname για να το αποφύγουμε.
function useBlurActiveElementOnRouteChangeWeb() {
  const pathname = usePathname();
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    (document.activeElement as HTMLElement | null)?.blur?.();
  }, [pathname]);
}

function RootNavigator() {
  const { initializing } = useAuth();
  const { languageReady } = useLanguage();
  const { colors, isDark } = useTheme();
  useProtectedRoute();
  useBlurActiveElementOnRouteChangeWeb();

  if (initializing || !languageReady) {
    return <AppSplash />;
  }

  return (
    <>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <MobileWebDownloadBanner />
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: colors.background },
          animation: 'default',
          gestureEnabled: true,
          animationTypeForReplace: 'push',
        }}
      />
    </>
  );
}

export default function RootLayout() {
  return (
    <GestureHandlerRootView style={styles.flex}>
      <SafeAreaProvider>
        <ThemeProvider>
          <AppAlertProvider>
            <LanguageProvider>
              <AuthProvider>
                <ProfileProvider>
                  <NeedsActionProvider>
                    <BugReportProvider>
                      <RootNavigator />
                    </BugReportProvider>
                  </NeedsActionProvider>
                </ProfileProvider>
              </AuthProvider>
            </LanguageProvider>
          </AppAlertProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
});
